@import '../../style/placeholder.scss';

:host {
    // Global
    --wcs-select-ligther-percentage: 40; // TODO: view to migrate to design tokens later (used on 'close' icon of a chip)
    --wcs-select-control-border-radius: var(--wcs-semantic-border-radius-base);

    --wcs-select-text-color-disabled: var(--wcs-semantic-color-text-disabled);
    --wcs-select-value-color: var(--wcs-semantic-color-text-primary);
    --wcs-select-value-font-weight: var(--wcs-semantic-font-weight-medium);

    --wcs-select-placeholder-color: var(--wcs-semantic-color-text-tertiary);
    --wcs-select-placeholder-font-weight: var(--wcs-semantic-font-weight-roman);
    --wcs-select-placeholder-font-style: var(--wcs-semantic-font-style-control-placeholder);

    // Options
    --wcs-select-options-padding: var(--wcs-semantic-spacing-base-150);

    // Control
    --wcs-select-control-line-height: var(--wcs-semantic-font-line-height-large);

    --wcs-select-control-background-color: var(--wcs-semantic-color-background-control-default);

    --wcs-select-control-arrow-color: var(--wcs-semantic-color-foreground-action-secondary-default);
    --wcs-select-control-arrow-color-disabled: var(--wcs-semantic-color-foreground-disabled);

    --wcs-select-control-chips-gap: var(--wcs-semantic-spacing-small);

    --wcs-select-control-border-color-default: var(--wcs-semantic-color-border-primary);
    --wcs-select-control-border-color-disabled: var(--wcs-semantic-color-border-disabled);
    --wcs-select-control-border-color-focus: var(--wcs-semantic-color-border-control-focus);
    --wcs-select-control-border-color-error: var(--wcs-semantic-color-border-critical);

    --wcs-select-control-border-style-default: solid;
    --wcs-select-control-border-style-error: solid;
    --wcs-select-control-border-style-focus: var(--wcs-semantic-border-style-focus-control);

    --wcs-select-control-border-width-default: var(--wcs-semantic-border-width-default);
    --wcs-select-control-border-width-focus: var(--wcs-semantic-border-width-large);

    --wcs-select-control-padding-horizontal-m: var(--wcs-semantic-spacing-large);
    --wcs-select-control-padding-horizontal-l: var(--wcs-semantic-spacing-large);

    --wcs-select-control-height: var(--wcs-select-control-height-m);
    --wcs-select-control-height-m: var(--wcs-semantic-size-m);
    --wcs-select-control-height-l: var(--wcs-semantic-size-l);
    --wcs-internal-select-chip-size: var(--wcs-semantic-size-s);
    --wcs-select-control-font-size: var(--wcs-select-control-font-size-m, 1rem);
    --wcs-select-control-font-size-m: var(--wcs-semantic-font-size-m);
    --wcs-select-control-font-size-l: var(--wcs-semantic-font-size-l);

    // Overlay
    --wcs-select-overlay-max-height: 360px;
    --wcs-select-overlay-background-color: var(--wcs-semantic-color-background-surface-primary);
    --wcs-select-overlay-border-width: var(--wcs-semantic-border-width-default);
    --wcs-select-overlay-border-color: var(--wcs-semantic-color-border-secondary);
    --wcs-select-overlay-border-radius: var(--wcs-semantic-border-radius-base);

    // Chips
    --wcs-select-control-chip-color-default: var(--wcs-semantic-color-foreground-action-primary-default);
    --wcs-select-control-chip-color-disabled: var(--wcs-semantic-color-text-disabled);
    --wcs-select-control-chip-background-color-default: var(--wcs-semantic-color-background-action-primary-default);
    --wcs-select-control-chip-background-color-disabled: var(--wcs-semantic-color-background-action-primary-disabled);
    --wcs-select-control-chip-padding-vertical: var(--wcs-semantic-spacing-small);
    --wcs-select-control-chip-padding-horizontal: calc(1.5 * var(--wcs-semantic-spacing-base));
    --wcs-select-control-chip-line-height: var(--wcs-semantic-font-line-height-large);
    --wcs-select-control-chip-border-radius: var(--wcs-semantic-border-radius-base);

    // Autocomplete
    --wcs-select-control-autocomplete-padding-vertical-m: var(--wcs-semantic-spacing-small);
    --wcs-select-control-autocomplete-padding-vertical-l: var(--wcs-semantic-spacing-small);

    // No Result Container
    --wcs-select-internal-noresult-padding-horizontal: var(--wcs-semantic-spacing-base);

    display: block;
    min-height: var(--wcs-select-control-height);
    position: relative;
    outline: none;
    user-select: none;
    box-sizing: border-box;

    // So the arrow on the right doesn't shrink when text overflows.
    svg {
        flex-shrink: 0;
    }

    .wcs-select-control {
        min-height: var(--wcs-select-control-height);
        outline: var(--wcs-select-control-border-width-default) var(--wcs-select-control-border-style-default) var(--wcs-select-control-border-color-default);
        outline-offset: calc(var(--wcs-select-control-border-width-default) * -1);
    }

    .arrow {
        fill: var(--wcs-select-control-arrow-color);
    }

    .chip {
        fill: var(--wcs-select-control-chip-color-default);
        opacity: calc((100 - var(--wcs-select-ligther-percentage)) / 100); // TODO, see later to migrate to design tokens

        &:hover {
            opacity: 100;
        }
    }
}

@mixin select-control-outline-focus {
    outline: var(--wcs-select-control-border-width-focus) var(--wcs-select-control-border-style-focus) var(--wcs-select-control-border-color-focus);
    outline-offset: calc(var(--wcs-select-control-border-width-focus) * -1);
}

@mixin select-control-outline-error {
    outline-style: var(--wcs-select-control-border-style-error);
    outline-color: var(--wcs-select-control-border-color-error);
}

@mixin select-control-outline-disabled {
    outline-style: var(--wcs-select-control-border-style-default);
    outline-color: var(--wcs-select-control-border-color-disabled);
}

.wcs-select-control {
    box-sizing: border-box;

    display: flex;
    align-items: center;

    background-color: var(--wcs-select-control-background-color);
    border-radius: var(--wcs-select-control-border-radius);
    outline: var(--wcs-select-control-border-color-default) solid var(--wcs-select-control-border-width-default); // because some theme might use default border
    outline-offset: calc(var(--wcs-select-control-border-width-default) * -1);

    font-size: var(--wcs-select-control-font-size, 1rem);
    line-height: var(--wcs-select-control-line-height);
    cursor: pointer;

    padding-left: var(--wcs-select-control-padding-horizontal-m);
    padding-right: calc(var(--wcs-select-control-padding-horizontal-m) - 4px); // 4px is the blank space inside the arrow svg on the right
}

label {
    &.wcs-select-placeholder {
        color: var(--wcs-select-placeholder-color);
        font-style: var(--wcs-select-placeholder-font-style);
        font-weight: var(--wcs-select-placeholder-font-weight);
    }

    &.wcs-select-value {
        color: var(--wcs-select-value-color);
        font-style: normal;
        font-weight: var(--wcs-select-value-font-weight);
    }

    cursor: pointer;
    transition: color 125ms ease-in;
    // Hide overflowing display text
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &::after {
        // https://stackoverflow.com/questions/51640141/font-style-italic-makes-text-protrude-from-container-cutting-off-text
        content: '';
        padding: 1px;
    }
}

.wcs-select-value-container {
    max-width: calc(100% - calc(24px + var(--wcs-semantic-spacing-base))); // 24px (width of arrow svg) + 8px of padding = 32px
    padding-right: 8px; // to avoid overflow on arrow

    display: flex;
    flex-direction: row;
    gap: var(--wcs-select-control-chips-gap);
    align-items: center;
    flex-wrap: wrap;
}

.wcs-select-chip {
    box-sizing: border-box;
    height: var(--wcs-internal-select-chip-size);
    line-height: var(--wcs-select-control-chip-line-height);
    color: var(--wcs-select-control-chip-color-default);
    background-color: var(--wcs-select-control-chip-background-color-default);
    border-radius: var(--wcs-select-control-chip-border-radius);
    display: flex;
    align-items: center;
    padding: var(--wcs-select-control-chip-padding-vertical) var(--wcs-select-control-chip-padding-horizontal);
    overflow: initial;
    z-index: 1;
}

.wcs-select-options {
    display: none;
    position: absolute;
    z-index: 999999999;

    border: solid var(--wcs-select-overlay-border-width) var(--wcs-select-overlay-border-color);
    border-radius: var(--wcs-select-overlay-border-radius);

    max-height: var(--wcs-select-overlay-max-height);
    overflow-y: auto;
    width: calc(100% - calc(calc(2 * var(--wcs-select-overlay-border-width)) + calc(2 * var(--wcs-select-options-padding))));
    padding: var(--wcs-select-options-padding);

    background-color: var(--wcs-select-overlay-background-color);
}

:host(.expanded) {
    outline-width: var(--wcs-select-control-border-width-focus);

    .wcs-select-control {
        @include select-control-outline-focus;
    }

    .wcs-select-options {
        display: block;
    }
}

:host([aria-invalid=true]), :host([autocomplete]):has(input[aria-invalid=true]) {
    .wcs-select-control {
        @include select-control-outline-error;
    }
}

:host(:not(.expanded):focus-visible) {
    outline-width: var(--wcs-select-control-border-width-focus);

    .wcs-select-control {
        @include select-control-outline-focus;
    }
}

:host([disabled]) {
    cursor: not-allowed;
    --wcs-select-control-background-color: var(--wcs-semantic-color-background-control-disabled);

    .wcs-select-control {
        @include select-control-outline-disabled;
    }

    .wcs-select-control,
    label {
        cursor: default;
        pointer-events: none;
    }

    label {
        color: var(--wcs-select-text-color-disabled);
    }

    .arrow {
        fill: var(--wcs-select-control-arrow-color-disabled);
    }

    .wcs-select-chip {
        color: var(--wcs-select-control-chip-color-disabled);
        background-color: var(--wcs-select-control-chip-background-color-disabled);
    }

    .chip {
        fill: var(--wcs-select-control-chip-color-disabled);
    }
}

:host([size=m]) {
    --wcs-select-control-font-size: var(--wcs-select-control-font-size-m);
    --wcs-select-control-height: var(--wcs-select-control-height-m);
    --wcs-internal-select-chip-size: var(--wcs-select-chip-size-m);

    .wcs-select-control {
        padding-left: var(--wcs-select-control-padding-horizontal-m);
        padding-right: calc(var(--wcs-select-control-padding-horizontal-m) - 4px); // 4px is the blank space inside the arrow svg on the right
    }

    &:host([autocomplete]) {
        .autocomplete-field {
            height: calc(var(--wcs-select-control-height) - var(--wcs-select-control-autocomplete-padding-vertical-m) * 2);
        }

        .wcs-select-value-container {
            // (select height - chips height) / 2 - border width
            padding-top: var(--wcs-select-control-autocomplete-padding-vertical-m);
            padding-bottom: var(--wcs-select-control-autocomplete-padding-vertical-m);
        }
    }
}

:host([size=l]) {
    --wcs-select-control-font-size: var(--wcs-select-control-font-size-l);
    --wcs-select-control-height: var(--wcs-select-control-height-l);
    --wcs-internal-select-chip-size: var(--wcs-select-chip-size-l);

    .wcs-select-control {
        padding-left: var(--wcs-select-control-padding-horizontal-l);
        padding-right: calc(var(--wcs-select-control-padding-horizontal-l) - 4px); // 4px is the blank space inside the arrow svg on the right
    }

    &:host([autocomplete]) {
        .autocomplete-field {
            height: calc(var(--wcs-select-control-height) - var(--wcs-select-control-autocomplete-padding-vertical-l) * 2);
        }

        .wcs-select-value-container {
            padding-top: var(--wcs-select-control-autocomplete-padding-vertical-l);
            padding-bottom: var(--wcs-select-control-autocomplete-padding-vertical-l);
        }
    }
}

:host([chips]) {
    .wcs-select-control {
        flex-wrap: wrap;
    }

    .wcs-select-value-container {
        margin-top: 4px;
        margin-bottom: 4px;
    }
}


:host([autocomplete]) {
    .wcs-select-value-container {
        margin: 0;
        width: 100%;
    }

    input {
        font: unset;
        background-color: transparent;
        outline: none;
        border: none;
        padding: unset;

        color: var(--wcs-select-value-color);
        font-weight: var(--wcs-select-value-font-weight) !important;
        font-size: var(--wcs-select-control-font-size);
        line-height: inherit;

        &::placeholder {
            @include placeholder(var(--wcs-select-placeholder-color), var(--wcs-select-placeholder-font-weight), var(--wcs-select-placeholder-font-style));
        }
    }
}

:host([autocomplete]:focus-within) {
    .wcs-select-control {
        @include select-control-outline-focus;
    }
}

.autocomplete-field {
    width: 0;
    min-width: 50px;
    flex-grow: 1;
}

.noresult-container {
    padding-left: var(--wcs-select-internal-noresult-padding-horizontal);
    padding-right: var(--wcs-select-internal-noresult-padding-horizontal);

    display: flex;
    flex-direction: row;
    align-items: center;
    height: var(--wcs-select-control-height);
}
